import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowUp, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export function StickyCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Hide CTA a bit earlier near the bottom and show "scroll to top" instead.
      const BOTTOM_BUFFER_PX = 520;
      const isNearBottom = windowHeight + currentScrollY >= documentHeight - BOTTOM_BUFFER_PX;
      
      if (isNearBottom) {
        setIsVisible(false);
        setShowScrollTop(currentScrollY > 520);
      } else if (currentScrollY > 520) {
        setIsVisible(true);
        setShowScrollTop(false);
      } else {
        setIsVisible(false);
        setShowScrollTop(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  if (user) return null;

  return (
    <AnimatePresence mode="wait">
      {isVisible && !showScrollTop && (
        <motion.div
          key="sticky-cta"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          className="fixed bottom-8 right-8 z-[60] hidden md:block"
        >
          <Link
            to="/auth?mode=signup"
            className="flex items-center gap-3 px-6 py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-105 transition-all group"
          >
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform">
              <Zap className="w-5 h-5 fill-current" />
            </div>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-[10px] uppercase tracking-widest opacity-80">Limited Offer</span>
              <span className="text-lg">Start Free Trial</span>
            </div>
          </Link>
        </motion.div>
      )}

      {showScrollTop && (
        <motion.div
          key="scroll-top"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          className="fixed bottom-8 right-8 z-[60] hidden md:block"
        >
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top"
            className="w-14 h-14 rounded-2xl bg-indigo-600 text-white shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-105 transition-all flex items-center justify-center"
          >
            <ArrowUp className="w-6 h-6" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
